import { useState } from "react";
import {
  ChatWidgetText,
  isSafeSwatchColor,
  isSafeWidgetImage,
  isSafeWidgetUrl,
  ProductItem,
  ProductsWidget,
} from "../../../Helper/ChatWidget";
import { ArrowUpRightIcon, CheckIcon, PlusIcon } from "../ChatWidgetIcons";
import {
  ChatWidgetAnswer,
  cx,
  openWidgetUrl,
  Rating,
} from "../ChatWidgetPrimitives";
import "./ProductsCard.scss";

const ProductImage = ({
  item,
  text,
}: {
  item: ProductItem;
  text: ChatWidgetText;
}) => {
  const image = isSafeWidgetImage(item.image) ? (
    <img src={item.image} alt="" loading="lazy" referrerPolicy="no-referrer" />
  ) : null;
  const badge = item.badge ? (
    <span className="cw-prod-tag">{item.badge}</span>
  ) : null;
  if (isSafeWidgetUrl(item.url)) {
    return (
      <button
        type="button"
        className="cw-prod-img cw-prod-img--link"
        aria-label={text("widgetViewItem", { name: item.title })}
        onClick={() => openWidgetUrl(item.url)}
      >
        {image}
        {badge}
      </button>
    );
  }
  return (
    <div className="cw-prod-img">
      {image}
      {badge}
    </div>
  );
};

const Product = ({
  item,
  isChosen,
  isOpened,
  isDimmed,
  isLocked,
  onAdd,
  text,
}: {
  item: ProductItem;
  isChosen: boolean;
  /** The cart link was opened in the store. The store never reports back, so this never says "Added". */
  isOpened: boolean;
  isDimmed: boolean;
  isLocked: boolean;
  onAdd: () => void;
  text: ChatWidgetText;
}) => {
  const hasCart = isSafeWidgetUrl(item.addToCartUrl);
  const swatches = (item.swatches ?? []).filter((swatch) =>
    isSafeSwatchColor(swatch.color),
  );
  return (
    <article
      className={cx(
        "cw-prod",
        isChosen && "cw-prod--on",
        isDimmed && "cw-prod--dim",
      )}
      aria-label={item.title}
    >
      <ProductImage item={item} text={text} />
      <div className="cw-prod-body">
        <div className="cw-strong cw-prod-title">{item.title}</div>
        {item.subtitle && <div className="cw-muted cw-sm">{item.subtitle}</div>}
        {swatches.length > 0 && (
          <ul
            className="cw-prod-swatches"
            aria-label={swatches.map((swatch) => swatch.label).join(", ")}
          >
            {swatches.map((swatch, index) => (
              <li
                key={index}
                className={cx(index === 0 && "cw-prod-swatch--on")}
                style={{ backgroundColor: swatch.color }}
                title={swatch.label}
              />
            ))}
          </ul>
        )}
        <div className="cw-row cw-prod-foot">
          <div className="cw-stack">
            {item.price && (
              <span className="cw-prod-price cw-num">
                {item.price}
                {item.compareAtPrice && (
                  <s className="cw-muted">{item.compareAtPrice}</s>
                )}
              </span>
            )}
            <Rating rating={item.rating} reviewCount={item.reviewCount} />
          </div>
          {hasCart && (!isLocked || isChosen || isOpened) && (
            <button
              type="button"
              className={cx(
                "cw-prod-add",
                (isChosen || isOpened) && "cw-prod-add--done",
              )}
              aria-label={
                isOpened
                  ? text("widgetOpenedInCart")
                  : text("widgetAddToBag", { name: item.title })
              }
              title={
                isOpened
                  ? text("widgetOpenedInCart")
                  : text("widgetAddToBag", { name: item.title })
              }
              disabled={isChosen || isOpened || isLocked}
              onClick={onAdd}
            >
              {isOpened ? (
                <ArrowUpRightIcon size={18} />
              ) : isChosen ? (
                <CheckIcon size={18} />
              ) : (
                <PlusIcon size={18} />
              )}
            </button>
          )}
        </div>
        {!hasCart && (!isLocked || isChosen) && (
          // No cart action: the pick goes to Kai as a question, never "Added".
          <button
            type="button"
            className={cx("cw-prod-ask", isChosen && "cw-prod-ask--done")}
            aria-label={
              isChosen
                ? text("widgetAskedAbout")
                : text("widgetAskAbout", { name: item.title })
            }
            disabled={isChosen || isLocked}
            onClick={onAdd}
          >
            {isChosen && <CheckIcon size={14} strokeWidth={2.6} />}
            {isChosen ? text("widgetAskedAbout") : text("widgetAskAboutThis")}
          </button>
        )}
      </div>
    </article>
  );
};

/**
 * 11 Products: a horizontal row of product cards reaching the gutter. "+"
 * (Add to bag) opens the shop's add-to-cart link in a new tab when the shop
 * has one (client only). The link adds to the shopper's existing cart; the
 * store never reports back, so the button then says "Opened in your cart",
 * never "Added";
 * without one the card offers "Ask about this", which sends the pick to Kai
 * and never claims anything was added. Answered: the pick stays marked, the
 * rest dims.
 */
export const ProductsCard = ({
  widget,
  onAnswer,
  text,
}: {
  widget: ProductsWidget;
  onAnswer: ChatWidgetAnswer;
  text: ChatWidgetText;
}) => {
  const [openedIds, setOpenedIds] = useState<string[]>([]);
  const answered = widget.response?.values?.selected;
  const chosenIds = Array.isArray(answered) ? answered : [];
  const isAnswered = !!widget.response;
  const items = widget.items ?? [];

  const add = (item: ProductItem) => {
    if (isSafeWidgetUrl(item.addToCartUrl)) {
      openWidgetUrl(item.addToCartUrl);
      setOpenedIds((current) =>
        current.includes(item.id) ? current : [...current, item.id],
      );
      return;
    }
    onAnswer("select", { selected: [item.id] }, item.title);
  };

  return (
    <div
      className={cx("cw-products", items.length === 1 && "cw-products--single")}
    >
      {widget.title && (
        <div className="cw-h cw-products-title">{widget.title}</div>
      )}
      <div className="cw-products-rail" role="list">
        {items.map((item) => {
          const isChosen = chosenIds.includes(item.id);
          const isOpened = openedIds.includes(item.id);
          return (
            <div role="listitem" className="cw-products-cell" key={item.id}>
              <Product
                item={item}
                isChosen={isChosen}
                isOpened={isOpened}
                isDimmed={isAnswered && !isChosen && !isOpened}
                isLocked={isAnswered}
                onAdd={() => add(item)}
                text={text}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};
